import React, { FC, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { prepareDropList, createPayload } from '../shared/orgSearch';
import { labelsSelector } from '../utils';
import { checkError } from '../booking/traveller-details/TravelDestination';
import { getManulifeID, getTranslationSpace } from '@trip-ux/common';

import {
  flipLanguage,
  BannerProps,
  TopNav,
  TopNavProps,
  SideNav,
  SideNavProps,
  Banner,
  useAdAuth
} from '@trip-ux/components';
import { NAV_BAR_USER_ITEMS, POLICY_ROUTES, SUPPORTED_LOCALES, AUTH_HEADERS } from '@trip-ux/constants';
import {
  ReduxStore,
  useActions,
  searchOrganization,
  getNavContent,
  setOrganizationCode,
  clearQuoteForm,
  getTmsConfiguration,
  getTmsTranslations,
  resetStore
} from '@trip-ux/redux-store';
import { isEmpty, get, find } from 'lodash';
import { WithRole } from './WithRole';

const NavBar: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { contentFragment, userProfile, lang, labels, config, organization } = useSelector((store: ReduxStore) => ({
    contentFragment: store.contentFragment,
    userProfile: store.userProfile,
    lang: store.config.locale,
    labels: labelsSelector(store),
    config: store.config,
    organization: store.organization
  }));

  const adAuthHook = useAdAuth();

  const navigate = useNavigate();

  const isAuthenticated = !isEmpty(userProfile);
  const actions = useActions({
    searchOrganization,
    getNavContent,
    setOrganizationCode,
    clearQuoteForm,
    getTmsTranslations,
    getTmsConfiguration,
    resetStore
  });

  const onLanguageToggle = useCallback(
    e => {
      e.preventDefault();
      flipLanguage(lang);
    },
    [lang]
  );

  const url = getManulifeID(config.options.manulifeId, lang);
  const onTopNavItemSelect = useCallback((navId: string, e: React.MouseEvent) => {
    e.preventDefault();

    switch (navId) {
      case NAV_BAR_USER_ITEMS.USER_LOGOUT:
        adAuthHook?.logout();
        break;
      case NAV_BAR_USER_ITEMS.MANULIFEID:
        window.location.href = url;
        break;
      case NAV_BAR_USER_ITEMS.PREFERENCES:
        window.location.href = config.options[`preferences${lang.toUpperCase()}`];
        break;

      default:
        break;
    }
  }, [adAuthHook, url, config, lang]);

  const organizationDropDownList = prepareDropList(organization.organizations, config);
  const organizationCode = config.options.organizationCode;
  const organizationSelectionChange = useCallback(
    (value: string) => {
      actions.resetStore();
      const translationSpace = getTranslationSpace(value);
      actions.setOrganizationCode(value, translationSpace).then(() => {
        window.location.reload();
        actions.clearQuoteForm();
        actions.getTmsTranslations(translationSpace, get(config, 'options.translation'), lang);
        actions.getTmsConfiguration(translationSpace, get(config, 'options.translationConfig'), lang);
        navigate(POLICY_ROUTES.QUOTE);
        actions.getNavContent();
      });
    },
    [actions, config, lang, navigate]
  );

  const searchOrganizations = (str: string) => actions.searchOrganization(createPayload(str, config));

  // @ts-ignore
  const bannerContent: BannerProps = find(
    get(contentFragment.banner, 'bannerContents'),
    content => get(content, 'bannerId') === config.bannerId
  );

  const isAdmin = get(config, 'isAdmin');
  const manulifeBranding = get(contentFragment.banner, 'manulifeBranding');

  let hasLogo = false;
  if (userProfile?.whiteLabel?.logoFile) {
    hasLogo = true;
  }

  const checkDisabledFunc = () => {
    const currentUrl = window.location.pathname;
    const paths = ['/external/contact-us', '/external/faqs'];
    return paths.includes(currentUrl);
  };

  const shouldClearLabels = isAdmin ? checkDisabledFunc() : false;
  const organizationLabel = shouldClearLabels ? undefined : get(labels, 'organization.labels');

  return isAdmin ? (
    <div className="d-flex flex-column">
      <TopNav
        {...(contentFragment.topNav as TopNavProps)}
        isAuthenticated={isAuthenticated}
        userName={get(userProfile, 'name')}
        userType={get(userProfile, 'userType')}
        onLanguageToggle={onLanguageToggle}
        contactInfo={get(contentFragment, 'contactUs')}
        onSelect={onTopNavItemSelect}
      />
      <div className="nav-layout">
        <WithRole.Menu
          sideNavContent={contentFragment.sideNav}
          render={({ sideNavContent }) => (
            <SideNav
              {...(sideNavContent as SideNavProps)}
              onSelect={() => true}
              isAuthenticated={isAuthenticated}
              userName={get(userProfile, 'name')}
              userType={get(userProfile, 'userType')}
              organizationCategory={get(userProfile, 'organizationCategory')}
              claims={userProfile.claims}
              onLanguageToggle={onLanguageToggle}
              contactInfo={get(contentFragment, 'contactUs')}
              contactBackButtonLabel={get(labels, 'side_nav.contact.contactBackButtonLabel')}
              organizationDropDownList={organizationDropDownList}
              organizationSelectionChange={organizationSelectionChange}
              searchOrganizations={searchOrganizations}
              organizationCode={organizationCode}
              showOrgSearch={get(config, 'isAdmin')}
              organizationText={get(labels, 'organization.labels.findAgency')}
              branding={manulifeBranding}
              featureFlags={get(userProfile, 'featureFlags')}
            />
          )}
        />
        <div className="sticky-cart-container">
          <Banner
            {...bannerContent}
            manulifeBranding={get(contentFragment.banner, 'manulifeBranding')}
            whiteLabelBranding={get(contentFragment.banner, 'whiteLabelBranding')}
            organizationDropDownList={organizationDropDownList}
            organizationSelectionChange={organizationSelectionChange}
            searchOrganizations={searchOrganizations}
            checkError={checkError}
            organizationCode={organizationCode}
            isAdmin={get(config, 'isAdmin')}
            userType={get(userProfile, 'userType')}
            featureFlags={get(userProfile, 'featureFlags')}
            organizationCategory={get(userProfile, 'organizationCategory')}
            organizationLabel={organizationLabel}
            showLabel={false}
            language={lang as SUPPORTED_LOCALES}
            hasLogo={hasLogo}
            isB2C={get(userProfile, 'salesChannel') === AUTH_HEADERS.SALES_CHANNEL_B2C}
            userProfile={userProfile}
            organizationName={`${userProfile[`organizationName${lang.toUpperCase()}`]} (${
              userProfile.organizationCode
            })`}
          />
          {children}
        </div>
      </div>
    </div>
  ) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>{children}</>
  );
};
NavBar.displayName = 'NavBar';
export default NavBar;
